<template>
  <div class="aap-wrap">
    <div class="page-head">
      <div>
        <h1 class="page-title">AI Agents Prompts</h1>
        <p class="page-sub">Configure chaque agent IA : provider, modèle, clé API, activation, et le prompt avec ses variables dynamiques et ses langues.</p>
      </div>
      <div class="head-meta">
        <strong>{{ activeCount }}</strong>
        agents actifs
      </div>
    </div>
    <div class="rule"></div>

    <LoadingSpinner v-if="loading" title="Chargement des prompts..." />
    <div v-else-if="error" class="state state-error">{{ error }}</div>

    <template v-else>
      <PromptCategory
        v-for="(group, index) in grouped"
        :key="group.category"
        :category="group.category"
        :prompts="group.prompts"
        :open-first="index === 0"
        :saving-id="savingId"
        :saved-id="savedId"
        :previewing-id="previewingId"
        @save="onSave"
        @toggle="onToggle"
        @preview="onPreview"
      />
    </template>
  </div>
</template>

<script>
import PromptCategory from '@/components/AiAgentsPrompts/PromptCategory.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { promptsApi } from '@/_services'
import { PROMPT_CATEGORIES, promptMetaForKey } from '@/components/AiAgentsPrompts/promptConfig.js'

export default {
  name: 'AiAgentsPrompts',
  components: { PromptCategory, LoadingSpinner },
  data() {
    return {
      prompts: [],
      loading: true,
      error: '',
      savingId: null,
      savedId: null,
      previewingId: null,
    }
  },
  computed: {
    activeCount() {
      return this.prompts.filter(prompt => prompt.enabled).length
    },
    grouped() {
      const byKey = new Map()
      this.prompts.forEach((prompt) => {
        const meta = promptMetaForKey(prompt.key)
        byKey.set(prompt.key, {
          ...prompt,
          name: meta && meta.name ? meta.name : prompt.name,
          description: meta && meta.description ? meta.description : prompt.description,
          category: meta && meta.category ? meta.category : (prompt.category || 'AI Agents'),
        })
      })

      const used = new Set()
      const groups = PROMPT_CATEGORIES.map((category) => {
        const prompts = category.prompts
          .map((item) => {
            const prompt = byKey.get(item.key)
            if (!prompt) return null
            used.add(item.key)
            return {
              ...prompt,
              name: item.name,
              description: item.description,
              category: category.category,
              allowDisabled: item.allowDisabled,
            }
          })
          .filter(Boolean)
        return { category: category.category, prompts }
      }).filter(group => group.prompts.length > 0)

      const unknownMap = new Map()
      byKey.forEach((prompt, key) => {
        if (used.has(key)) return
        const category = prompt.category || 'AI Agents'
        if (!unknownMap.has(category)) unknownMap.set(category, [])
        unknownMap.get(category).push(prompt)
      })
      unknownMap.forEach((prompts, category) => groups.push({ category, prompts }))

      return groups
    },
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      this.error = ''
      promptsApi.list()
        .then((items) => {
          this.prompts = Array.isArray(items) ? items : []
          this.loading = false
        })
        .catch(() => {
          this.error = 'Impossible de charger les prompts. Réessayez.'
          this.loading = false
        })
    },
    onSave(updated) {
      if (this.savingId !== null) return
      this.error = ''
      this.savedId = null
      this.savingId = updated.id
      promptsApi.save(updated)
        .then((saved) => {
          const item = saved || updated
          const index = this.prompts.findIndex(prompt => prompt.id === item.id)
          if (index !== -1) {
            this.$set(this.prompts, index, item)
          }
          this.savedId = item.id
          window.setTimeout(() => {
            if (this.savedId === item.id) this.savedId = null
          }, 2000)
          this.savingId = null
          return this.$swal({
            type: 'success',
            title: this.$t('lang.label.great'),
            text: this.$t('lang.label.datasavedsuccessfully'),
            showConfirmButton: false,
            timer: 1500,
          })
        })
        .catch(() => {
          this.error = "L'enregistrement a échoué."
        })
        .then(() => {
          this.savingId = null
        })
    },
    onToggle({ id, enabled }) {
      this.error = ''
      promptsApi.toggle(id, enabled)
        .then(() => {
          const prompt = this.prompts.find(item => item.id === id)
          if (prompt) prompt.enabled = enabled
        })
        .catch(() => {
          this.error = 'Le changement de statut a échoué.'
        })
    },
    onPreview({ id, content }) {
      if (this.previewingId !== null) return
      this.error = ''
      this.previewingId = id
      promptsApi.preview(id, content)
        .then((result) => {
          return this.$swal({
            type: 'success',
            title: `${result.provider} / ${result.model}`,
            text: result.text || 'Aucune réponse générée.',
            confirmButtonText: 'Fermer',
          })
        })
        .catch((err) => {
          const message = typeof err === 'string' ? err : (err && err.error ? err.error : 'La prévisualisation a échoué.')
          this.$swal({ type: 'error', title: 'Erreur IA', text: message })
        })
        .then(() => {
          this.previewingId = null
        })
    },
  },
}
</script>

<style scoped>
.aap-wrap { max-width: 1080px; margin: 0 auto; padding: 40px 28px 80px; color: #16161A; font-family: 'Inter', system-ui, sans-serif; }
.page-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; }
.page-title { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 34px; letter-spacing: .5px; text-transform: uppercase; margin: 0; line-height: 1; }
.page-sub { color: #56565E; font-size: 13px; margin-top: 10px; max-width: 560px; }
.head-meta { text-align: right; font-size: 12px; color: #56565E; white-space: nowrap; }
.head-meta strong { display: block; font-family: 'Oswald', sans-serif; font-size: 22px; color: #16161A; font-weight: 600; }
.rule { height: 1px; background: #E9E7E0; margin: 24px 0 30px; }
.state { padding: 40px; text-align: center; color: #56565E; }
.state-error { color: #B7791F; }
@media (max-width: 767px) {
  .aap-wrap { padding: 24px 14px 60px; }
  .page-head { display: block; }
  .head-meta { text-align: left; margin-top: 14px; }
}
</style>
